import { Button, Tabs, TabsList, TabsTrigger } from "@devhop/ui";
import { addDays, addMonths, addWeeks, format } from "date-fns";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  BookingSlot,
  PublicRoom,
  TimetableItem,
  WorkCalendarDayOff,
} from "./types";
import { PublicTimetableDayView } from "./PublicTimetableDayView";
import { PublicTimetableMonthView } from "./PublicTimetableMonthView";
import { PublicTimetableWeekView } from "./PublicTimetableWeekView";
import { getMonthRange, getWeekRange, toYMD } from "./timetable-utils";

export type TimableView = "month" | "week" | "day";

export type PublicTimetableProps = {
  items: TimetableItem[];
  /** ວ່າງ = ໂໝດປະຕິທິນວຽກ (ກິດຈະກຳຕາມເວລາ, ບໍ່ມີຫ້ອງ) */
  rooms?: PublicRoom[];
  onSlotSelect: (slot: BookingSlot) => void;
  onEventClick?: (item: TimetableItem) => void;
  onDayClickFromMonth?: (date: string) => void;
  conflictIds?: Set<string>;
  onRangeChange?: (start: string, end: string) => void;
  initialView?: TimableView;
  initialDate?: Date;
  /** ວັນພັກ/ວັນພິເສດ (mgmt_work_calendar) ເພື່ອສະແດງໃນເບິ່ງເດືອນ */
  dayOffs?: WorkCalendarDayOff[];
};

export function PublicTimetable({
  items,
  rooms = [],
  onSlotSelect,
  onEventClick,
  onDayClickFromMonth,
  conflictIds,
  onRangeChange,
  initialView = "day",
  initialDate = new Date(),
  dayOffs = [],
}: PublicTimetableProps) {
  const [view, setView] = useState<TimableView>(initialView);
  const [currentDate, setCurrentDate] = useState(
    () => new Date(initialDate.getTime()),
  );

  const weekRange = useMemo(() => getWeekRange(currentDate), [currentDate]);
  const monthRange = useMemo(() => getMonthRange(currentDate), [currentDate]);

  useEffect(() => {
    if (!onRangeChange) return;
    if (view === "month") {
      onRangeChange(toYMD(monthRange.start), toYMD(monthRange.end));
    } else if (view === "week") {
      onRangeChange(toYMD(weekRange.start), toYMD(weekRange.end));
    } else {
      const d = toYMD(currentDate);
      onRangeChange(d, d);
    }
  }, [view, currentDate, monthRange, weekRange, onRangeChange]);

  const dateLabel = useMemo(() => {
    if (view === "month") {
      return format(currentDate, "MMM yyyy");
    }
    if (view === "week") {
      return `${format(weekRange.start, "d MMM")} – ${format(weekRange.end, "d MMM yyyy")}`;
    }
    return format(currentDate, "d MMM yyyy");
  }, [view, currentDate, weekRange]);

  const handlePrev = useCallback(() => {
    if (view === "month") {
      setCurrentDate((d) => addMonths(d, -1));
    } else if (view === "week") {
      setCurrentDate((d) => addWeeks(d, -1));
    } else {
      setCurrentDate((d) => addDays(d, -1));
    }
  }, [view]);

  const handleNext = useCallback(() => {
    if (view === "month") {
      setCurrentDate((d) => addMonths(d, 1));
    } else if (view === "week") {
      setCurrentDate((d) => addWeeks(d, 1));
    } else {
      setCurrentDate((d) => addDays(d, 1));
    }
  }, [view]);

  const handleDayClickFromMonth = useCallback(
    (dateStr: string) => {
      if (onDayClickFromMonth) {
        onDayClickFromMonth(dateStr);
      } else {
        setCurrentDate(new Date(dateStr));
        setView("day");
      }
    },
    [onDayClickFromMonth],
  );

  const itemsInRange = useMemo(() => {
    if (view === "day") {
      const d = toYMD(currentDate);
      return items.filter((i) => i.date === d);
    }
    if (view === "week") {
      const start = toYMD(weekRange.start);
      const end = toYMD(weekRange.end);
      return items.filter((i) => i.date >= start && i.date <= end);
    }
    const start = toYMD(monthRange.start);
    const end = toYMD(monthRange.end);
    return items.filter((i) => i.date >= start && i.date <= end);
  }, [view, currentDate, items, weekRange, monthRange]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs value={view} onValueChange={(v) => setView(v as TimableView)}>
          <TabsList className="grid w-full max-w-[240px] grid-cols-3">
            <TabsTrigger value="month">ເດືອນ</TabsTrigger>
            <TabsTrigger value="week">ສັບປະດານ</TabsTrigger>
            <TabsTrigger value="day">ວັນ</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handlePrev}
            aria-label="ກ່ອນໜ້າ"
          >
            <ChevronLeftIcon className="size-4" />
          </Button>
          <span className="min-w-[180px] text-center font-medium text-sm">
            {dateLabel}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleNext}
            aria-label="ຖັດໄປ"
          >
            <ChevronRightIcon className="size-4" />
          </Button>
        </div>
      </div>

      {view === "month" && (
        <PublicTimetableMonthView
          monthDate={currentDate}
          items={itemsInRange}
          onDayClick={handleDayClickFromMonth}
          dayOffs={dayOffs}
        />
      )}
      {view === "week" && (
        <PublicTimetableWeekView
          weekStart={new Date(weekRange.start)}
          items={itemsInRange}
          rooms={rooms}
          onSlotSelect={onSlotSelect}
          onEventClick={onEventClick}
          conflictIds={conflictIds}
        />
      )}
      {view === "day" && (
        <PublicTimetableDayView
          date={toYMD(currentDate)}
          items={itemsInRange}
          rooms={rooms}
          onSlotSelect={onSlotSelect}
          onEventClick={onEventClick}
          conflictIds={conflictIds}
        />
      )}
    </div>
  );
}
